<template>
  <v-row dense class="file-edit-content">
    <v-col xs="12" sm="12" md="12">
      <file-comment-dialog
        active-customer-view
        :rowStatuses="rowStatuses"
        @refresh="getRows"
        ref="commentDialog"
      />

      <v-row dense>
        <v-col cols="12">
          <FileHeaderCustomer
            ref="fileHeader"
            :result="result"
            :statistics="result.statistics"
            :errors="result.errors"
            :task="sessionStatus.task"
            :rowStatuses="rowStatuses"
            :sessionStatus="sessionStatus"
            @onFiltersChanged="onFiltersChanged"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col xs="12" sm="12" md="12">
          <v-tabs v-model="tab" color="primary" background-color="transparent">
            <v-tab v-for="tab in tabsItems" :key="tab.id">
              {{ tab.title }}
            </v-tab>
          </v-tabs>
          <v-divider class="pt-5" horizontal />
          <v-card flat>
            <FileTableCustomer
              ref="fileTableCustomer"
              role="customer"
              @showComments="showComments"
              @setStatus="setStatus"
              @onPaginationChange="getRows"
              :headerName="selectedTab.title"
              :rows="rows"
              :headers="selectedTab.headers"
              :params="selectedTab.params"
              :filters="filters"
              :loading="loading"
              :totalItems="totalItems"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import _ from 'lodash';
import FileTableCustomer from '../../components/files/FileTableCustomer';
import FileCommentDialog from '../../components/files/FileCommentDialog';
import FileHeaderCustomer from '../../components/files/FileHeaderCustomer';

import { GlobalConfig } from '@/shared/GlobalAppconfig.class';
import { CUSTOMER_TABS, DEFAULTS } from '@/constants';
import { Axios } from '@/services';
import { verifyToken } from '@/api';
import { processingResultsApiV1, rowStatusesV1 } from '@/api/v1';
import { processingResultsApiV2, rowStatusesV2 } from '@/api/v2';

export default {
  computed: {},
  data: () => ({
    accessTokenHashId: undefined,
    result_id: undefined,
    environment: undefined,
    tabsItems: CUSTOMER_TABS,
    tab: 0,
    loading: false,
    filters: {},
    rowStatuses: [],
    rows: [],
    task_id: undefined,
    totalItems: 0,
    result: {
      errors: [],
      task: {
        upload: {
          file: {}
        },
        business: {}
      }
    },
    sessionStatus: {
      re_match_status: 'IN_PROGRESS',
      analyze_status: 'IN_PROGRESS',
      import_status: 'IN_PROGRESS',
      analyze_rows_ids: [],
      import_rows_ids: [],
      task: {}
    },
    selectedTab: CUSTOMER_TABS[0]
  }),
  async mounted() {
    this.accessTokenHashId = this.$route.query.access_token;
    this.result_id = this.$route.params.result_id;
    await this.verifyAccess();
    await this.init();
  },
  watch: {
    tab(newVal) {
      this.selectedTab = this.tabsItems[newVal];
      this.$refs.fileTableCustomer.resetPagination();
      this.onFiltersChanged();
    }
  },
  components: {
    FileHeaderCustomer,
    FileTableCustomer,
    FileCommentDialog
  },
  methods: {
    async verifyAccess() {
      const access_token = await verifyToken(
        this.result_id,
        this.accessTokenHashId,
        DEFAULTS.LINKS_TYPES_MAP.CUSTOMER_LINK
      );

      Axios.Api.setToken(access_token);
    },
    async init() {
      this.sessionStatus = await this.getAnalyzeAndImportStatuses();
      await this.refresh({ withResult: true, withRowStatuses: true });
    },
    onFiltersChanged(filters = {}) {
      if (filters) this.filters = filters;

      if (filters.showPrice !== this.filters.showPrice) {
        return;
      }

      return this.refresh();
    },
    openFile(file) {
      window.open(file.location, '_blank');
    },
    refresh(
      opt = {
        withResult: false,
        withRowStatuses: false
      }
    ) {
      return Promise.all([
        this.getRows(false),
        opt.withResult ? this.getResult() : Promise.resolve(),
        opt.withRowStatuses ? this.getRowStatuses() : Promise.resolve()
      ])
        .catch((err) => {
          console.log('Refresh Error: ', err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async getRowStatuses() {
      const { items } = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? rowStatusesV2
          : rowStatusesV1
      ).fetchRowStatuses();

      this.rowStatuses = items;
    },
    getResult() {
      return (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      )
        .fetchResult(this.$route.params.result_id)
        .then((response) => {
          this.result = response;
          this.task_id = this.result.task.id;
          GlobalConfig.config.businessName = this.result.task.business.name;
          GlobalConfig.config.taskName = this.result.task.name;
        });
    },
    getRows(stopLoadingOnCall = true) {
      this.loading = true;
      const pagination = this.$refs.fileTableCustomer.getPagination();

      return (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      )
        .fetchRows(this.$route.params.result_id, {
          ...this.getListQueryParameters(),
          page: pagination.page || 1,
          rowsPerPage: pagination.itemsPerPage,
          sortBy: pagination.sortBy,
          descending:
            pagination.sortDesc &&
            pagination.sortDesc.length &&
            pagination.sortDesc[0]
              ? true
              : undefined
        })
        .then((response) => {
          this.rows = response.items;

          if (response.pagination) {
            this.totalItems = response.pagination.rowCount;
          }

          if (stopLoadingOnCall) {
            this.loading = false;
          }
        });
    },
    setStatus({ status, multimatch, correct_rows_ids }) {
      this.loading = true;
      const selectedRow = multimatch
        ? undefined
        : this.$refs.fileTableCustomer.getSelectedRow();

      return (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      )
        .updateResultRowStatus({
          resultId: this.$route.params.result_id,
          rowIds: selectedRow ? [selectedRow.id] : undefined,
          status,
          filters: {
            multimatch: multimatch ? DEFAULTS.MULTIMATCH_FIELDS : undefined,
            multimatch_value: multimatch ? multimatch : undefined,
            correct_rows_ids
          }
        })
        .then((newStatus) => {
          if (selectedRow) {
            const row = _.find(this.rows, { id: selectedRow.id });

            row.status = newStatus;
            row.status_id = newStatus.id;

            this.getStatistics();

            this.loading = false;
          } else {
            this.refresh();
          }
        });
    },
    getAnalyzeAndImportStatuses() {
      return (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      ).fetchAnalyzeAndImportStatuses(
        this.$route.params.result_id,
        this.environment
      );
    },
    showComments() {
      const selected = this.$refs.fileTableCustomer.getSelectedRow();
      this.$refs.commentDialog.open(selected);
    },
    getListQueryParameters() {
      return {
        multimatch: this.filters.multimatch
          ? DEFAULTS.MULTIMATCH_FIELDS
          : undefined,
        exclude: {
          ...(this.selectedTab.params && this.selectedTab.params.exclude
            ? this.selectedTab.params.exclude
            : {}),
          statusesNames: this.filters.excludeIgnore ? ['ignore'] : undefined
        },
        ...this.selectedTab.params,
        search: this.filters.search || undefined,
        statuses:
          this.filters.statuses && this.filters.statuses.length
            ? this.filters.statuses
            : undefined,
        reviewed: this.filters.reviewed || undefined,
        customerVerified: this.filters.customerVerified || undefined
      };
    },
    addComment() {
      const selected = this.$refs.fileTableCustomer.getSelectedRow();
      this.$refs.commentDialog.open(selected);
    },
    getStatistics() {
      return (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      )
        .fetchResultStatistics(this.$route.params.result_id)
        .then((statistics) => {
          this.result.statistics = statistics;
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }
};
</script>
